import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, MessageCircle, Send } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Reveal, FadeIn } from "../components/Motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const INFO = [
  {
    icon: MapPin,
    title: "Visit Us",
    lines: ["Old Post Office, 1st Floor,", "near Water Tank, Guthalu Road,", "Mandya — 571403, Karnataka"],
  },
  { icon: Phone, title: "Call Us", lines: ["+91 79750 94721"], href: "tel:+917975094721", testid: "contact-phone-link" },
  { icon: Mail, title: "Email", lines: ["magdaleenajoy@gmail.com"], href: "mailto:magdaleenajoy@gmail.com", testid: "contact-email-link" },
  { icon: Clock, title: "Hours", lines: ["Monday – Saturday", "9:00 AM – 6:00 PM"] },
];

export default function Contact() {
  const [courses, setCourses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", course: "", message: "" });

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.course) {
      toast.error("Please fill your name, phone and choose a course.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...form, email: form.email || null, message: form.message || null });
      toast.success("Enquiry sent! We will reach out to you shortly.");
      setForm({ name: "", phone: "", email: "", course: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 lg:pt-40">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">Contact & Visit</p>
        </FadeIn>
        <h1 className="mt-4 max-w-3xl font-serif text-4xl font-bold leading-[1.08] tracking-tight text-forest-deep sm:text-5xl lg:text-6xl">
          <Reveal y={24}>Come, let's talk</Reveal>
          <Reveal y={24} delay={0.1}>
            <span className="italic text-terracotta">over a cup of coffee.</span>
          </Reveal>
        </h1>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="contact-info-cards">
          {INFO.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="h-full rounded-3xl border border-forest/10 bg-white p-7" data-testid={`contact-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <c.icon className="h-6 w-6 text-terracotta" />
                <h3 className="mt-4 font-serif text-xl font-semibold text-forest-deep">{c.title}</h3>
                {c.lines.map((l) =>
                  c.href ? (
                    <a key={l} href={c.href} data-testid={c.testid} className="mt-1 block text-sm text-stone-600 hover:text-terracotta">
                      {l}
                    </a>
                  ) : (
                    <p key={l} className="mt-1 text-sm text-stone-600">{l}</p>
                  ),
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-testid="enquiry-section">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <form onSubmit={submit} data-testid="enquiry-form" className="rounded-3xl border border-forest/10 bg-white p-8 shadow-sm sm:p-10">
              <h2 className="font-serif text-3xl font-semibold text-forest-deep">Quick Enquiry</h2>
              <p className="mt-2 text-sm text-stone-500">We respond within one working day.</p>
              <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="enq-name">Name *</Label>
                  <Input id="enq-name" data-testid="enquiry-name-input" value={form.name} onChange={set("name")} placeholder="Your name" className="mt-2 h-11" />
                </div>
                <div>
                  <Label htmlFor="enq-phone">Mobile *</Label>
                  <Input id="enq-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="mt-2 h-11" />
                </div>
                <div>
                  <Label htmlFor="enq-email">Email</Label>
                  <Input id="enq-email" type="email" data-testid="enquiry-email-input" value={form.email} onChange={set("email")} placeholder="you@example.com" className="mt-2 h-11" />
                </div>
                <div>
                  <Label htmlFor="enq-course">Course of Interest *</Label>
                  <select
                    id="enq-course"
                    data-testid="enquiry-course-select"
                    value={form.course}
                    onChange={set("course")}
                    className="mt-2 flex h-11 w-full rounded-md border border-input bg-white px-3 text-sm text-forest-deep focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Select course</option>
                    {courses.map((c) => (
                      <option key={c.id} value={c.title}>{c.title}</option>
                    ))}
                    <option value="Franchise Opportunity">Franchise Opportunity</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="enq-message">Message</Label>
                  <Textarea id="enq-message" data-testid="enquiry-message-input" value={form.message} onChange={set("message")} placeholder="Tell us what you'd like to know..." className="mt-2 min-h-[110px]" />
                </div>
                <input type="text" name="website" value={form.website || ""} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-testid="enquiry-honeypot" />
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button type="submit" disabled={submitting} data-testid="enquiry-submit-button" className="rounded-full bg-forest px-8 py-6 font-display text-cream hover:bg-forest-deep disabled:opacity-60">
                  {submitting ? "Sending..." : (<>Send Enquiry <Send className="ml-2 h-4 w-4" /></>)}
                </Button>
                <Button asChild variant="outline" data-testid="enquiry-whatsapp-button" className="rounded-full border-forest/30 px-8 py-6 font-display text-forest hover:bg-forest hover:text-cream">
                  <a href="https://wa.me/917975094721" target="_blank" rel="noreferrer">
                    <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
                  </a>
                </Button>
              </div>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="h-full min-h-[420px] overflow-hidden rounded-3xl border-[10px] border-white shadow-xl shadow-forest/10" data-testid="contact-map">
              <iframe
                title="St Mary's Charitable Trust — Mandya"
                src="https://www.google.com/maps?q=Guthalu+Road,+Mandya,+Karnataka+571403&output=embed"
                className="h-full min-h-[420px] w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
